import { DateTime } from "luxon";
import React from "react";
import { useNavigate } from "react-router";
import { usePatch, usePost } from "../../../../api/useCall";
import useInput from "@ui/lib/hooks/useInput";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import type { Setup } from "../../../../api/useSetups";
import { useMutateBody } from "../../../../api/useQuery";
import InputV2 from "@ui/Input/InputV2";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import { toPath } from "../../../../router/helpers/path";
import { formatDateToYYYYMMDD } from "diagnostics-lib/dates";

interface P {
  setups: Setup[];
  setup?: Setup;
  projectId: string;
  onRequestClose: () => void;
  timeZone: string;
  okButtonName: string;
}

const CreateUpdateModalForm = ({
  setups,
  setup,
  projectId,
  onRequestClose,
  timeZone,
  okButtonName,
}: P) => {
  // Input handlers

  const isFirstSetup =
    setups.length === 0 || setups.find(s => s.startDate === null)?.id === setup?.id;
  const DEFAULT_FIRST_SETUP_NAME = "V1";
  const currentSetups = React.useRef(setups);
  const mutateSetups = useMutateBody("/projects/:projectId(uuid)/setups", {
    routeParams: { projectId },
  });

  const {
    value: nameValue,
    onChange: handleNameChanged,
    dirty: isInputNameDirty,
    defaultValue: nameDefaultValue,
  } = useInput(setup?.name ?? (setups.length ? `V${setups.length + 1}` : DEFAULT_FIRST_SETUP_NAME));
  const {
    value: descriptionValue,
    onChange: handleDescriptionChanged,
    dirty: isInputDescriptionDirty,
    defaultValue: descriptionDefaultValue,
  } = useInput(setup?.description ?? undefined);
  const {
    value: startDateValue,
    onChange: handleStartDateValueChanged,
    dirty: isStartedDateDirty,
    defaultValue: startDateDefaultValue,
  } = useInput(
    setup?.startDate ? formatDateToYYYYMMDD(new Date(setup.startDate)).toString() : undefined,
  );

  const errorInputName: string | undefined = React.useMemo(() => {
    if ((isFirstSetup || setup) && !nameValue.length)
      return t("setups.modal.create_or_edit.input.name.error.cannot_be_empty");
    if (isInputNameDirty && currentSetups.current.find(s => s.name === nameValue.trim()))
      return t("setups.modal.create_or_edit.input.name.error.name_already_exists");
    if (isLeft(short(io.string).decode(nameValue))) return t("commons.input.error.text_to_long");
    return;
  }, [isFirstSetup, isInputNameDirty, nameValue, setup]);

  const errorInputDescription: string | undefined = React.useMemo(() => {
    if (descriptionValue.length > 1000) return t("commons.input.error.text_to_long");
    return;
  }, [descriptionValue.length]);

  const setupStartDates = setups
    .map(setup => {
      const date = setup.startDate ? new Date(setup.startDate) : null;
      if (date === null) return null;
      return new Date(date.setDate(date.getDate() + 1)).toISOString().split("T")[0] ?? null;
    })
    .filter(d => d !== null);

  const setupWithSameStartDate = React.useMemo(
    () =>
      setups.find(
        currentSetup =>
          currentSetup.id !== setup?.id &&
          currentSetup.startDate !== null &&
          formatDateToYYYYMMDD(new Date(currentSetup.startDate)).toString() === startDateValue,
      ),
    [setup?.id, setups, startDateValue],
  );

  const errorStartedDate: string | undefined = React.useMemo(() => {
    if (!isFirstSetup && startDateValue.length < 1)
      return t("setups.modal.create_or_edit.input.startDate.error.cannot_be_empty");
    if (!isFirstSetup && setupWithSameStartDate)
      return t("setups.modal.create_or_edit.input.startDate.error.already_exists");
    if (
      !isFirstSetup &&
      !setup &&
      new Date(startDateValue).getTime() <
        new Date(setupStartDates[setupStartDates.length - 1] as string).getTime()
    )
      return t("setups.modal.create_or_edit.input.startDate.error.should_be_greater");
    return;
  }, [isFirstSetup, setup, setupStartDates, setupWithSameStartDate, startDateValue]);

  const [submitted, setSubmitted] = React.useState(false);

  const dirty = React.useMemo(
    () =>
      (nameValue.length > 0 &&
        (submitted || isInputNameDirty || isInputDescriptionDirty || isStartedDateDirty)) ||
      (isFirstSetup && nameValue.length > 0 && nameValue === DEFAULT_FIRST_SETUP_NAME),
    [
      isFirstSetup,
      isInputDescriptionDirty,
      isInputNameDirty,
      isStartedDateDirty,
      nameValue,
      submitted,
    ],
  );

  const valid = React.useMemo((): boolean => {
    const noErrorOnInput = [errorInputName, errorInputDescription, errorStartedDate].every(
      e => e === undefined,
    );
    return noErrorOnInput;
  }, [errorInputDescription, errorInputName, errorStartedDate]);

  const createSetup = usePost("/projects/:projectId(uuid)/setups");
  const updateSetup = usePatch("/projects/:projectId(uuid)/setups/:setupId(uuid)");
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const [showDateError, setDateShowError] = React.useState(false);

  const handleSubmit = React.useCallback(async () => {
    if (!valid) return false;
    setSubmitted(true);
    const currentStartDateValue = setup?.startDate
      ? formatDateToYYYYMMDD(new Date(setup.startDate)).toString()
      : undefined;
    const hasUpdatedStartDate =
      !isFirstSetup && startDateValue.length > 0 && startDateValue !== currentStartDateValue;

    const startDateInUTC =
      !isFirstSetup && startDateValue.length > 0
        ? DateTime.fromJSDate(new Date(startDateValue), { zone: timeZone })
            .set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
              year: new Date(startDateValue).getUTCFullYear(),
              month: new Date(startDateValue).getUTCMonth() + 1,
              day: new Date(startDateValue).getUTCDate(),
            })
            .toUTC()
            .toJSDate()
        : undefined;

    const updateSetupData = {
      name: setup?.name === nameValue ? undefined : nameValue.trim(),
      description: setup?.description === descriptionValue ? undefined : descriptionValue,
      startDate: hasUpdatedStartDate ? startDateInUTC : undefined,
    };

    // create setup
    const newSetupData = {
      name: nameValue.trim(),
      description: descriptionValue.length ? descriptionValue : null,
      startDate: isFirstSetup ? null : (startDateInUTC ?? null),
    };

    const res = setup
      ? await updateSetup({
          body: updateSetupData,
          routeParams: { projectId, setupId: setup.id },
        })
      : await createSetup({ body: newSetupData, routeParams: { projectId } });

    if (res.status === 201) {
      mutateSetups(cache => [...cache, res.body]);
    }

    if (res.status === 201 || res.status === 204) {
      addToast(
        "success",
        setup
          ? t("setup.modal.update_setup.toast", {
              setupName: updateSetupData.name ?? setup.name,
              name: isInputNameDirty,
              description: isInputDescriptionDirty,
            })
          : t("setup.modal.add_setup.toast", {
              name: newSetupData.name,
            }),
      );
    }

    // got to created setup page on success
    if (res.status === 201) {
      void navigate(
        toPath("/projects/:projectId/setup/:setupId", {
          projectId,
          setupId: res.body.id,
        }),
        {
          replace: true,
        },
      );
    }

    return true;
  }, [
    addToast,
    createSetup,
    descriptionValue,
    isFirstSetup,
    isInputDescriptionDirty,
    isInputNameDirty,
    mutateSetups,
    nameValue,
    navigate,
    projectId,
    setup,
    !isFirstSetup,
    startDateValue,
    timeZone,
    updateSetup,
    valid,
  ]);

  return (
    <ModalForm
      onSubmit={dirty && valid ? handleSubmit : false}
      onRequestClose={onRequestClose}
      okButtonName={okButtonName}
    >
      <InputV2
        label={t("project.settings.setup.add_update_setup.modal.title.name")}
        value={nameValue}
        onChange={handleNameChanged}
        dirty={isInputNameDirty}
        defaultValue={isFirstSetup ? undefined : nameDefaultValue}
        placeholder={t("project.settings.setup.add_update_setup.modal.placeholder.name")}
        error={errorInputName}
        required
        autoFocus
      />
      <InputV2
        label={t("project.settings.setup.add_update_setup.modal.title.description")}
        value={descriptionValue}
        dirty={isInputDescriptionDirty}
        defaultValue={isFirstSetup ? undefined : setup ? descriptionDefaultValue : ""}
        onChange={handleDescriptionChanged}
        placeholder="Description"
      />
      {!isFirstSetup && (
        <>
          <InputV2
            label={t("project.settings.setup.add_update_setup.modal.title.effective_start")}
            value={startDateValue}
            onChange={handleStartDateValueChanged}
            type="date"
            dirty={isStartedDateDirty}
            defaultValue={setup ? startDateDefaultValue : undefined}
            error={showDateError ? errorStartedDate : undefined}
            required
            min={setup ? undefined : setupStartDates[setupStartDates.length - 1]}
            onClick={() => setDateShowError(true)}
          />
        </>
      )}
    </ModalForm>
  );
};

export default React.memo(CreateUpdateModalForm);
